import { useRef, useState } from 'react';
import { ballRadiusRatios } from '@photo-merge/shared';
import { BallPreview } from './BallPreview';
import type { Photo } from './photos';
import styles from './ArrangeStep.module.css';

interface ArrangeStepProps {
  photos: Photo[];
  onReorder: (photos: Photo[]) => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}

/** Preview diameter for the largest ball, in CSS pixels. */
const MAX_PREVIEW = 96;
const MIN_PREVIEW = 34;

function move<T>(items: T[], from: number, to: number): T[] {
  const next = items.slice();
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

export function ArrangeStep({ photos, onReorder, onEdit, onRemove }: ArrangeStepProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // Distinguishes a tap (open the cropper) from a drag (reorder).
  const moved = useRef(false);

  const ratios = ballRadiusRatios(photos.length);
  const largest = ratios.at(-1) ?? 1;
  const previewSize = (rank: number) => {
    const relative = (ratios[rank - 1] ?? largest) / largest;
    return Math.round(MIN_PREVIEW + (MAX_PREVIEW - MIN_PREVIEW) * relative);
  };

  /** Finds which slot is under the pointer, so touch drags work like mouse drags. */
  function slotUnder(x: number, y: number): number | null {
    const el = document.elementFromPoint(x, y)?.closest('[data-slot]');
    const raw = el?.getAttribute('data-slot');
    return raw == null ? null : Number(raw);
  }

  // A ball that came back from a saved game has no original to re-crop.
  const anySaved = photos.some((photo) => photo.savedKey);

  return (
    <div>
      <p className={styles.intro}>
        Smallest first. Drag to reorder, or use the arrows.{' '}
        {anySaved
          ? 'Tap a photo you just added to crop it; ones already saved keep their crop.'
          : 'Tap a photo to crop it.'}
      </p>

      <div className={styles.row}>
        {photos.map((photo, index) => {
          const rank = index + 1;
          const size = previewSize(rank);
          const croppable = !photo.savedKey;
          return (
            <div key={photo.id} className={styles.slot} data-slot={index}>
              <button
                className={[
                  styles.ballButton,
                  dragIndex === index ? styles.dragging : '',
                  overIndex === index && dragIndex !== index ? styles.dropTarget : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ width: size, height: size }}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setDragIndex(index);
                  moved.current = false;
                }}
                onPointerMove={(e) => {
                  if (dragIndex === null) return;
                  moved.current = true;
                  setOverIndex(slotUnder(e.clientX, e.clientY));
                }}
                onPointerUp={(e) => {
                  const target = slotUnder(e.clientX, e.clientY);
                  if (!moved.current) {
                    if (croppable) onEdit(index);
                  } else if (dragIndex !== null && target !== null && target !== dragIndex) {
                    onReorder(move(photos, dragIndex, target));
                  }
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onPointerCancel={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                aria-label={croppable ? `Ball ${rank}. Tap to crop.` : `Ball ${rank}`}
              >
                <BallPreview photo={photo} rank={rank} count={photos.length} size={size} />
                <span className={styles.rank}>{rank}</span>
              </button>

              <button
                className={styles.remove}
                onClick={() => onRemove(index)}
                aria-label={`Remove ball ${rank}`}
                title="Remove"
              >
                ×
              </button>

              {/*
                A guaranteed path to reorder. Dragging a small circle on a phone is
                fiddly, and this is genuinely faster for moving one photo a step.
              */}
              <div className={styles.nudges}>
                <button
                  className={styles.nudge}
                  disabled={index === 0}
                  onClick={() => onReorder(move(photos, index, index - 1))}
                  aria-label={`Move ball ${rank} smaller`}
                >
                  ◀
                </button>
                <button
                  className={styles.nudge}
                  disabled={index === photos.length - 1}
                  onClick={() => onReorder(move(photos, index, index + 1))}
                  aria-label={`Move ball ${rank} bigger`}
                >
                  ▶
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
